<template>
	<div
		ref="documentRef"
		class="document-container"
		:class="{ 'document-ec-reprise': isSecondeDeliberationFlag }"
	>
		<template v-if="loading || rattrapageData.length === 0">
			<div class="rattrapage-page-frame">
				<div class="document-header">
					<div class="logo-container">
						<img
							src="/images/logo-bulletin.jpg"
							alt="Dakar Institute of Technology"
							class="logo"
							@load="onLogoLoad"
							@error="onLogoError"
							crossorigin="anonymous"
						/>
					</div>
				</div>
				<h1 class="document-title">
					<span class="document-title-main">{{ documentLabels.feuilleDocumentTitle }}</span>
					<template v-if="sessionCodeDisplay">
						<span class="document-title-sep" aria-hidden="true">—</span>
						<span class="document-title-session">{{ sessionCodeDisplay }}</span>
					</template>
					<template v-if="semestreDisplay">
						<span class="document-title-sep" aria-hidden="true">—</span>
						<span class="document-title-session">{{ semestreDisplay }}</span>
					</template>
				</h1>
				<div v-if="loading" class="loading">
					<p>Chargement des données...</p>
				</div>
				<div v-else class="no-data">
					<p>{{ documentLabels.feuilleEmptyMessage }}</p>
				</div>
			</div>
		</template>
		<template v-else>
			<div
				v-for="(batch, batchIdx) in ecBatches"
				:key="batchIdx"
				class="rattrapage-page-frame rattrapage-batch-root"
			>
				<div class="document-header">
					<div class="logo-container">
						<img
							src="/images/logo-bulletin.jpg"
							alt="Dakar Institute of Technology"
							class="logo"
							@load="onLogoLoad"
							@error="onLogoError"
							crossorigin="anonymous"
						/>
					</div>
				</div>

				<h1 class="document-title">
					<span class="document-title-main">{{ documentLabels.feuilleDocumentTitle }}</span>
					<template v-if="sessionCodeDisplay">
						<span class="document-title-sep" aria-hidden="true">—</span>
						<span class="document-title-session">{{ sessionCodeDisplay }}</span>
					</template>
					<template v-if="semestreDisplay">
						<span class="document-title-sep" aria-hidden="true">—</span>
						<span class="document-title-session">{{ semestreDisplay }}</span>
					</template>
				</h1>

				<div class="rattrapage-table-container">
					<table class="rattrapage-table">
						<thead>
							<tr>
								<th v-for="ec in batch" :key="ec.ec_id" class="ec-header">
									{{ ec.ec_intitule }}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(row, rowIndex) in tableRowsForBatch(batch)" :key="rowIndex">
								<td v-for="ec in batch" :key="ec.ec_id" class="student-cell">
									<span v-if="row[ec.ec_id]" class="student-matricule">
										{{ row[ec.ec_id] }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import { useDeliberation } from '@/composables/useDeliberation'
import { useSessionUser } from '@/composables/useSessionUser'
import { loadRattrapageOutcomeItems } from '@/utils/deliberationRattrapageHelpers'
import { buildMapByLaravelInscriptionId } from '@/utils/inscriptionIdHelpers'
import { fetchAllEtudiantsBisForSessionSpecialite, warnEtudiantsBisTruncated } from '@/utils/etudiantsBisPagination'
import {
	getDeliberationOutcomeDocumentLabels,
	isSecondeDeliberation,
} from '@/utils/deliberationDocumentLabels'

/** Lisibilité renforcée vs résultats session ; max 6 EC par page paysage. */
const MAX_ECS_PER_PAGE = 6

const props = defineProps<{
	deliberation: Deliberation
	/** `snapshotPayload.raw` — si absent, chargé automatiquement pour une délib. validée. */
	snapshotRaw?: Record<string, unknown> | null
}>()

type EcColumn = {
	ec_id: number
	ec_code?: string
	ec_intitule: string
	ue_code?: string
	ue_intitule?: string
	nombre_etudiants?: number
}

const documentRef = ref<HTMLElement | null>(null)
const logoLoaded = ref(false)
const { getRattrapageDeliberationOutcome, getDeliberationSnapshot } = useDeliberation()
const { fetchEtudiantsBisPageSilent } = useSessionUser()

const rattrapageData = ref<any[]>([])
const loading = ref(true)
const matriculeMap = ref<Map<number, string>>(new Map())

const isSecondeDeliberationFlag = computed(() =>
	isSecondeDeliberation(props.deliberation.type_deliberation)
)
const documentLabels = computed(() =>
	getDeliberationOutcomeDocumentLabels(props.deliberation.type_deliberation)
)

function onLogoLoad() {
	logoLoaded.value = true
}

function onLogoError() {
	console.warn('Logo failed to load, continuing without it')
	logoLoaded.value = true
}

/** Code session affiché à côté du titre (même logique que les noms de PDF délibération). */
const sessionCodeDisplay = computed(() => {
	const raw = props.deliberation.session?.code ?? props.deliberation.session?.nom ?? ''
	const s = String(raw).trim()
	return s || ''
})

/** Semestre affiché à côté du code session (ex. « Semestre 1 »). */
const semestreDisplay = computed(() => {
	const num = props.deliberation.semestre?.numero
	if (num == null || num === '') return ''
	return `Semestre ${num}`
})

const ecsList = computed((): EcColumn[] => {
	return rattrapageData.value.map((item) => ({
		ec_id: item.ec_id,
		ec_code: item.ec_code,
		ec_intitule: item.ec_intitule,
		ue_code: item.ue_code,
		ue_intitule: item.ue_intitule,
		nombre_etudiants: item.nombre_etudiants
	}))
})

const ecBatches = computed((): EcColumn[][] => {
	const list = ecsList.value
	const batches: EcColumn[][] = []
	for (let i = 0; i < list.length; i += MAX_ECS_PER_PAGE) {
		batches.push(list.slice(i, i + MAX_ECS_PER_PAGE))
	}
	return batches
})

function tableRowsForBatch(batch: EcColumn[]): Record<number, string>[] {
	if (batch.length === 0) return []

	const maxEtudiants = Math.max(
		0,
		...batch.map((col) => {
			const item = rattrapageData.value.find((d) => d.ec_id === col.ec_id)
			return item?.etudiants?.length || 0
		})
	)

	const rows: Record<number, string>[] = []

	for (let i = 0; i < maxEtudiants; i++) {
		const row: Record<number, string> = {}
		for (const col of batch) {
			const ec = rattrapageData.value.find((d) => d.ec_id === col.ec_id)
			if (ec?.etudiants?.[i]) {
				const etudiant = ec.etudiants[i]
				const inscriptionId = Number(etudiant.inscription_id)
				const matricule =
					matriculeMap.value.get(inscriptionId) || etudiant.matricule || `#${inscriptionId}`
				row[col.ec_id] = matricule
			}
		}
		rows.push(row)
	}

	return rows
}

async function loadMatricules() {
	try {
		const { results: allStudents, truncated } = await fetchAllEtudiantsBisForSessionSpecialite(
			fetchEtudiantsBisPageSilent,
			props.deliberation.session_id,
			props.deliberation.specialite_id
		)
		warnEtudiantsBisTruncated(truncated, 'feuille de rattrapage')

		matriculeMap.value = buildMapByLaravelInscriptionId(allStudents, (student) => {
			const matricule = student.matricule || student.code || null
			return matricule ? String(matricule).trim() : ''
		})
	} catch (error) {
		console.error('Erreur lors du chargement des matricules:', error)
	}
}

async function loadRattrapageData() {
	loading.value = true
	try {
		await loadMatricules()

		const isSeconde = isSecondeDeliberationFlag.value
		const useSnapshot = props.deliberation.etat === 'valide'

		const { items, error } = await loadRattrapageOutcomeItems({
			isSecondeDeliberation: isSeconde,
			snapshotRaw: props.snapshotRaw,
			fetchSnapshotRaw:
				useSnapshot && !props.snapshotRaw
					? async () => {
							const r = await getDeliberationSnapshot(props.deliberation.id)
							return r.success ? (r.data?.raw as Record<string, unknown> | undefined) : null
						}
					: undefined,
			fetchLive: () => getRattrapageDeliberationOutcome(props.deliberation.id),
		})

		if (error) {
			console.error('Erreur lors du chargement des données de rattrapage:', error)
		}
		rattrapageData.value = items as any[]
	} catch (error) {
		console.error('Erreur lors du chargement des données de rattrapage:', error)
	} finally {
		loading.value = false
	}
}

onMounted(() => {
	loadRattrapageData()
})

defineExpose({
	documentRef,
	isReady: computed(() => !loading.value && logoLoaded.value)
})
</script>

<style scoped>
.document-container {
	width: 297mm;
	padding: 0;
	background: white;
	font-family: 'Times New Roman', Times, serif;
	box-sizing: border-box;
	color: #000;
	overflow: visible;
}

/*
 * Même logique que .page (20mm) dans ResultatsSessionDeliberation : ce bloc est cloné
 * page par page pour le PDF ; le padding doit être ici, pas seulement sur .document-container.
 */
.rattrapage-page-frame {
	box-sizing: border-box;
	width: 297mm;
	max-width: 297mm;
	padding: 16mm 20mm 18mm;
}

.rattrapage-batch-root {
	margin-bottom: 12mm;
}

.rattrapage-batch-root:last-child {
	margin-bottom: 0;
}

.document-header {
	text-align: center;
	margin-bottom: 12mm;
	display: flex;
	flex-direction: column;
	align-items: center;
}

.logo-container {
	background-color: transparent;
	padding: 0;
	border-radius: 0;
	display: inline-block;
	margin: 0 auto 5mm auto;
	text-align: center;
}

.logo {
	max-width: 200px;
	width: 200px;
	height: auto;
	display: block;
	object-fit: contain;
}

.document-title {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	justify-content: center;
	column-gap: 0.5em;
	row-gap: 0.15em;
	text-align: center;
	font-size: 16px;
	font-weight: bold;
	line-height: 1.25;
	margin-bottom: 10mm;
	color: #000;
}

.document-title-main {
	text-transform: uppercase;
}

.document-title-sep {
	flex-shrink: 0;
	opacity: 0.9;
}

.document-title-session {
	text-transform: none;
	font-weight: bold;
}

.loading,
.no-data {
	text-align: center;
	padding: 28mm 0;
	font-size: 16px;
	line-height: 1.4;
	color: #666;
}

.rattrapage-table-container {
	display: flex;
	justify-content: center;
	overflow-x: visible;
	width: 100%;
}

.rattrapage-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
	font-size: 15px;
	line-height: 1.35;
	margin-bottom: 6mm;
	border: 1px solid #000;
}

.rattrapage-table thead {
	background-color: #fff;
}

.rattrapage-table th,
.rattrapage-table td {
	border: 1px solid #000;
	padding: 7px 10px;
	text-align: center;
	vertical-align: top;
	background-color: #fff;
	word-break: break-word;
	white-space: normal;
}

.rattrapage-table th {
	padding: 8px 10px;
	font-weight: bold;
	font-size: 15px;
}

.ec-header {
	min-width: 0;
	font-weight: bold;
	font-size: 15px;
	line-height: 1.3;
	color: #000;
	vertical-align: middle;
	white-space: normal;
}

.student-cell {
	min-height: 26px;
	vertical-align: top;
}

.student-matricule {
	display: block;
	font-size: 15px;
	line-height: 1.35;
	padding: 2px 0;
	color: #000;
	text-align: center;
}

/* 2e délibération : issue finale — en-têtes comme les résultats de session */
.document-ec-reprise .rattrapage-table thead,
.document-ec-reprise .rattrapage-table th {
	background-color: #002c40;
	color: #fff;
}

.document-ec-reprise .ec-header {
	color: #fff;
}
</style>
